/**
 * Timeout Utility for Auth Service Calls
 *
 * Purpose: Prevent indefinite hangs on slow network connections
 * Wraps promises with configurable timeout using Promise.race
 *
 * @example
 * ```typescript
 * const user = await withTimeout(
 *   authService.getCurrentUser(),
 *   10000,
 *   'getCurrentUser'
 * );
 * ```
 */

/**
 * Custom error class for timeout errors
 * Allows differentiation between timeout and other errors
 */
export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TimeoutError);
    }
  }
}

/**
 * Wraps a promise with a timeout
 * Rejects with TimeoutError if promise doesn't resolve within specified time
 *
 * @param promise - The promise to wrap with timeout
 * @param timeoutMs - Timeout duration in milliseconds
 * @param errorMessage - Custom error message for timeout (optional)
 * @returns Promise that resolves with original value or rejects with TimeoutError
 *
 * @example
 * ```typescript
 * // Auth service call with 10-second timeout
 * try {
 *   const user = await withTimeout(
 *     fetch('/api/auth/user'),
 *     10000,
 *     'getCurrentUser'
 *   );
 * } catch (error) {
 *   if (error instanceof TimeoutError) {
 *     // Handle timeout - return cached user
 *     console.warn('Auth check timed out, using cached user');
 *     return getCachedUser();
 *   }
 *   throw error; // Other errors propagate
 * }
 * ```
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage?: string
): Promise<T> {
  let timeoutId: NodeJS.Timeout;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const message = errorMessage
        ? `${errorMessage} timed out after ${timeoutMs}ms`
        : `Operation timed out after ${timeoutMs}ms`;
      reject(new TimeoutError(message));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    // Clean up timeout timer when promise settles (either resolves or rejects)
    clearTimeout(timeoutId);
  });
}

/**
 * Recommended timeout constants for different operation types
 */
export const TIMEOUT_CONSTANTS = {
  /** Auth service calls - balance between UX and reliability */
  AUTH_CHECK: 10000, // 10 seconds

  /** Critical API calls requiring fresh data */
  API_CRITICAL: 10000, // 10 seconds

  /** Non-critical API calls where cached data acceptable */
  API_NORMAL: 5000, // 5 seconds

  /** File uploads - longer timeout for large files */
  FILE_UPLOAD: 30000, // 30 seconds

  /** Quick health checks */
  HEALTH_CHECK: 3000, // 3 seconds
} as const;
